import React, { useState, useContext } from "react";
import {
  format,
  startOfWeek,
  addDays,
  startOfMonth,
  endOfMonth,
  endOfWeek,
  isSameMonth,
  isSameDay,
  subMonths,
  addMonths,
  isBefore,
} from "date-fns";
import {
  hostDateToString,
  dateStringAddDays,
  daysBetweenDateStrings,
} from "../../inspect/util";
import { AuthContext } from "../../../../../auth-context";
import { DashboardContext } from "../../../dashboard-context";
import _ from "lodash";
import styled from "styled-components";
import Icon from "@material-ui/core/Icon";
import CalendarMenu from "./calendar-menu";

const Container = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0px 0px 8px 0px #e1e2e6;
  overflow-x: auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;

  button {
    border: none;
    border-radius: 4px;
    box-shadow: 0px 0px 8px 0px #e1e2e6;
    background: #fff;
    padding: 4px;
    margin: 0px 3px;
    cursor: pointer;

    &:hover {
      background: #f7f7f7;
    }
  }

  h2 {
    margin-left: 24px;
    font-size: 1.25rem;
  }
`;

const Week = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
`;

const Day = styled.div`
  border-right: ${(props) => (props.noBorder ? "none" : "1px solid #EEE")};
  border-bottom: 1px solid #eee;
  height: 110px;
  width: 100%;
  min-width: 140px;
  padding: 4px;
  cursor: pointer;
  color: ${(props) =>
    props.isSelected ? "unset" : !props.sameMonth ? "#9e9e9e" : "unset"};
  background: ${(props) => (props.isSelected ? "#f0fff4" : "none")};
  position: relative;
  &:hover {
    background: #f2f3f7;
    transition: 0.2s ease background;
  }

  span {
    border: ${(props) => (props.isToday ? "1px solid #4cb050" : "none")};
    color: ${(props) => (props.isToday ? "#4cb050" : "none")};
    display: flex;
    font-size: 0.9rem;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 2px;
    right: 2px;
    width: 24px;
    height: 24px;
    border-radius: 100px;
  }
`;

const WeekdayNames = styled(Day)`
  background: #070c29;
  /* border: none; */
  color: #fff;
  cursor: default;
  display: flex;
  font-size: 0.8rem;
  font-weight: bold;
  justify-content: center;
  align-items: center;
  height: 34px;
  border-bottom: none;
  /* text-transform: uppercase; */
  &:hover {
    background: #070c29;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  p {
    font-size: 0.8rem;
    font-weight: bold;
    border-radius: 100px;
    padding: 2px 8px;
    margin-bottom: 2px;
    width: calc(100% - 28px);
    max-width: 170px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  .andMore {
    color: unset;
    background: none;
    font-size: 0.75rem;
    font-weight: bold;
  }

  span {
    display: block;
  }
`;

export const Calendar = () => {
  const { user } = useContext(AuthContext);
  const { filterGroupID, clientsTrainerID } = useContext(DashboardContext);
  const [activeDate, setActiveDate] = useState(new Date());
  const [anchorEl, setAnchorEl] = useState("");
  const [menuData, setMenuData] = useState("");

  const getUserCheckins = (currentDate) => {
    let accounts = _.cloneDeep(user.accounts);
    const usersCheckin = [];
    const todayTemp = new Date()
    const today = new Date(todayTemp.getFullYear(), todayTemp.getMonth(), todayTemp.getDate())
    
    accounts.forEach((entry, index) => {
      const lastWeighInDateTemp = new Date(entry.report_date);
      const lastWeighInDate = new Date(
        lastWeighInDateTemp.getUTCFullYear(),
        lastWeighInDateTemp.getUTCMonth(),
        lastWeighInDateTemp.getUTCDate()
      );
      let userCheckinDate = hostDateToString(lastWeighInDate);
      // Default check-in date
      userCheckinDate = dateStringAddDays(userCheckinDate, 7);
      const lastWeighInDatePlus5Days = new Date(lastWeighInDate.getFullYear(), lastWeighInDate.getMonth(), lastWeighInDate.getDate() + 5)
      const nextPossibleWeighInDate = new Date(lastWeighInDatePlus5Days.valueOf() > today.valueOf() ? lastWeighInDatePlus5Days.valueOf() : today.valueOf())
      let desiredDayOfWeek = null

      if (entry.check_in_date) {
        const checkInDateTemp = new Date(entry.check_in_date);
        const checkInDate = new Date(
          checkInDateTemp.getUTCFullYear(),
          checkInDateTemp.getUTCMonth(),
          checkInDateTemp.getUTCDate()
        );
        desiredDayOfWeek = checkInDate.getDay()
        let dayOffset = desiredDayOfWeek - nextPossibleWeighInDate.getDay()
        if(dayOffset < 0) {
          dayOffset += 7
        }
        const desiredNextWeighInDate = new Date(nextPossibleWeighInDate.getFullYear(), nextPossibleWeighInDate.getMonth(), nextPossibleWeighInDate.getDate() + dayOffset)

        userCheckinDate = hostDateToString(desiredNextWeighInDate);
      }

      if (
        entry.report_date &&
        entry.trainer_permission &&
        entry.claimed === 1 &&
        (filterGroupID === 0 || entry.groups.includes(filterGroupID)) &&
        (clientsTrainerID === "" ||
          entry.trainers.includes(clientsTrainerID)) &&
        daysBetweenDateStrings(
          hostDateToString(currentDate),
          userCheckinDate
        ) === 0
      ) {
        usersCheckin.push({
          id: entry.id,
          name: entry.name,
          vacation_mode_info: entry.vacation_mode_info,
        });
      }
    });

    return usersCheckin;
  };

  const getHeader = () => {
    return (
      <Header>
        <button onClick={() => setActiveDate(subMonths(activeDate, 1))}>
          <Icon>chevron_left</Icon>
        </button>
        <button onClick={() => setActiveDate(addMonths(activeDate, 1))}>
          <Icon>chevron_right</Icon>
        </button>
        <h2>{format(activeDate, "MMMM yyyy")} (Next check-in dates)</h2>
      </Header>
    );
  };

  const getWeekDaysNames = () => {
    const weekStartDate = startOfWeek(activeDate);
    const weekDays = [];
    for (let day = 0; day < 7; day++) {
      weekDays.push(
        <WeekdayNames key={day} noBorder={day === 6}>
          {format(addDays(weekStartDate, day), "E")}
        </WeekdayNames>
      );
    }
    return <Week>{weekDays}</Week>;
  };

  const generateDatesForCurrentWeek = (date, activeDate) => {
    let currentDate = date;
    const week = [];
    for (let day = 0; day < 7; day++) {
      const userData = getUserCheckins(currentDate);
      week.push(
        <Day
          key={currentDate.toString()}
          sameMonth={isSameMonth(currentDate, activeDate)}
          isToday={isSameDay(currentDate, new Date())}
          noBorder={day === 6}
          onClick={(e) => {
            if (userData.length !== 0) {
              setAnchorEl(e.currentTarget);
              setMenuData(userData);
            }
          }}
        >
          {userData && (
            <List
              isBeforeToday={
                isBefore(currentDate, new Date()) &&
                !isSameDay(currentDate, new Date())
              }
            >
              {userData.slice(0, 3).map((entry) => {
                const isBeforeToday =
                  isBefore(currentDate, new Date()) &&
                  !isSameDay(currentDate, new Date());
                  
                let color = "#333";

                if (isBeforeToday) {
                  color = "#dc544a";
                }

                return (
                  <p
                    key={entry.id}
                    style={{
                      color,
                    }}
                  >
                    {entry.name}
                  </p>
                );
              })}
              {userData.length > 3 && (
                <p className="andMore">{userData.length - 3} more</p>
              )}
            </List>
          )}
          <span>{format(currentDate, "d")}</span>
        </Day>
      );
      currentDate = addDays(currentDate, 1);
    }
    return week;
  };

  const getDates = () => {
    const startOfTheSelectedMonth = startOfMonth(activeDate);
    const endOfTheSelectedMonth = endOfMonth(activeDate);
    const startDate = startOfWeek(startOfTheSelectedMonth);
    const endDate = endOfWeek(endOfTheSelectedMonth);

    let currentDate = startDate;

    const allWeeks = [];

    while (currentDate <= endDate) {
      allWeeks.push(generateDatesForCurrentWeek(currentDate, activeDate));
      currentDate = addDays(currentDate, 7);
    }

    return <Week>{allWeeks}</Week>;
  };

  return (
    <>
      {getHeader()}
      <Container>
        {getWeekDaysNames()}
        {getDates()}
      </Container>
      <CalendarMenu
        menuData={menuData}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl("");
        }}
      />
    </>
  );
};

export default Calendar;
